/**
 * Resolves the Convex deployment URLs from the two vars the build injects.
 *
 * Both are set by `convex deploy`, and the site URL is NOT derivable from the cloud URL: Convex
 * takes it from the deployment's canonical URLs, so a custom HTTP-action domain makes them
 * unrelated hosts. Deriving one from the other by rewriting `.cloud` to `.site` therefore holds
 * only by coincidence, and when it breaks it breaks quietly — a request to a host that is not in
 * the generated `connect-src`, or to a different deployment whose signing key is not the one
 * registered with the vendor.
 *
 * `src/csp-config.json` maps both vars into `connect-src`, so anything resolved here is covered by
 * the generated policy.
 */
export const resolveConvexSiteUrl = (): string | undefined => {
  const configured = import.meta.env.VITE_CONVEX_SITE_URL;

  if (configured) {
    return configured;
  }

  // Derive only as a fallback. The cloud host does not serve HTTP actions at all, so returning it
  // unchanged would send every request to a host that cannot answer — worse than an assumption that
  // holds for standard deployments and is only wrong for a custom HTTP-action domain, which is
  // exactly the case where the site URL above is set.
  const cloudUrl = import.meta.env.VITE_CONVEX_URL?.trim();

  return cloudUrl ? cloudUrl.replace(/\.cloud\/?$/i, '.site') : undefined;
};

export const resolveConvexCloudUrl = (): string | undefined =>
  import.meta.env.VITE_CONVEX_URL ?? import.meta.env.VITE_CONVEX_SITE_URL;
